import get from 'lodash/get';

import { actionsEnum, actionTypes, entityTypes } from 'pages/store/actions/defaults';
import { listNormalizer, listTransformer, normalizer, transformer } from 'pages/store/schema';
import { submitErrors } from 'pages/utils/errors';

const normalizeData = (entity, list, data) => {
  if (!list && entity in normalizer) {
    return normalizer[entity](data);
  }
  if (list && entity in listNormalizer) {
    return listNormalizer[entity](data);
  }
  return {
    entities: {
      [entity]: data,
    },
  };
};

const transformData = (entity, list, data) => {
  if (!list && entity in transformer) {
    return transformer[entity](data);
  }

  if (list && entity in listTransformer) {
    return listTransformer[entity](data);
  }

  return data;
};

export const processing = ({ entity, list, actionType, status, errors, cancelToken } = {}) => ({
  type: actionsEnum.DATA_PROCESSING,
  entity,
  list,
  actionType,
  status,
  errors,
  cancelToken,
});

export const getSuccess = ({ response, entity, list }) => ({
  type: actionsEnum.DATA_GET_SUCCESS,
  entity,
  list,
  meta: response.meta,
  links: response.links,
  data: normalizeData(entity, list, response.data),
});

export const postSuccess = ({ response, entity, list }) => ({
  type: actionsEnum.DATA_POST_SUCCESS,
  entity,
  list,
  meta: response.meta,
  links: response.links,
  data: normalizeData(entity, list, response.data),
});

export const putSuccess = ({ response, entity, list }) => ({
  type: actionsEnum.DATA_PUT_SUCCESS,
  entity,
  list,
  meta: response.meta,
  links: response.links,
  data: normalizeData(entity, list, response.data),
});

export const deleteSuccess = ({ id, entity, list }) => ({
  type: actionsEnum.DATA_DELETE_SUCCESS,
  entity,
  list,
  id,
});

export const clearData = (clearEntities) => ({
  type: actionsEnum.DATA_CLEAR,
  clearEntities,
});

export const clearError = (clearEntities) => ({
  type: actionsEnum.ERROR_CLEAR,
  clearEntities,
});

export const getData =
  ({ url, entity, list = false, options = {}, onSuccess = () => {} }) =>
  async (dispatch, getState, { axios }) => {
    const processingState = getState().processing;
    let cancelToken = get(
      processingState,
      `${entity}.${list ? entityTypes.LIST : entityTypes.SINGLE}.${actionTypes.LOADING}.cancelToken`
    );
    if (cancelToken && cancelToken.cancel) {
      cancelToken.cancel();
    }
    cancelToken = axios.CancelToken.source();
    dispatch(
      processing({ entity, list, actionType: actionTypes.LOADING, status: true, cancelToken })
    );
    try {
      const response = await axios.get(url, { ...options, cancelToken: cancelToken.token });
      dispatch(getSuccess({ response: transformData(entity, list, response.data), entity, list }));
      dispatch(processing({ entity, list, actionType: actionTypes.LOADING, status: false }));
      onSuccess(response.data);
    } catch (errors) {
      if (!axios.isCancel(errors)) {
        dispatch(
          processing({ entity, list, actionType: actionTypes.LOADING, status: false, errors })
        );
        submitErrors(get(errors, 'response.data.errors', []));
      }
    }
  };

export const postData =
  ({ url, payload, entity, list = false, options = {}, onSuccess = () => {} }) =>
  async (dispatch, getState, { axios }) => {
    dispatch(processing({ entity, list, actionType: actionTypes.SAVING, status: true }));
    try {
      const response = await axios.post(url, payload, options);
      dispatch(postSuccess({ response: transformData(entity, list, response.data), entity, list }));
      dispatch(processing({ entity, list, actionType: actionTypes.SAVING, status: false }));
      onSuccess(response.data);
    } catch (errors) {
      dispatch(processing({ entity, list, actionType: actionTypes.SAVING, status: false, errors }));
      submitErrors(get(errors, 'response.data.errors', []));
    }
  };

export const putData =
  ({ url, payload, entity, list = false, options = {}, onSuccess = () => {} }) =>
  async (dispatch, getState, { axios }) => {
    dispatch(processing({ entity, list, actionType: actionTypes.SAVING, status: true }));
    try {
      const response = await axios.put(url, payload, options);
      dispatch(putSuccess({ response: transformData(entity, list, response.data), entity, list }));
      dispatch(processing({ entity, list, actionType: actionTypes.SAVING, status: false }));
      onSuccess(response.data);
    } catch (errors) {
      dispatch(processing({ entity, list, actionType: actionTypes.SAVING, status: false, errors }));
      submitErrors(get(errors, 'response.data.errors', []));
    }
  };

export const deleteData =
  ({ url, id, payload, entity, list = false, options = {}, onSuccess = () => {} }) =>
  async (dispatch, getState, { axios }) => {
    dispatch(processing({ entity, list, actionType: actionTypes.DELETING, status: true }));
    try {
      const response = await axios.delete(url, payload ? { data: payload } : {}, options);
      if (id) dispatch(deleteSuccess({ id, entity, list }));
      dispatch(processing({ entity, list, actionType: actionTypes.DELETING, status: false }));
      onSuccess(response.data);
    } catch (errors) {
      dispatch(
        processing({ entity, list, actionType: actionTypes.DELETING, status: false, errors })
      );
      submitErrors(get(errors, 'response.data.errors', []));
    }
  };
